import { Box, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { formatDateTimeReadable } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { RefreshIconButton } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/components/RefreshIconButton';
import { useCheckoutSession } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useCheckoutSession';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { ProcessingStrategyChooseButton } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/components/NoLenderRequestId/ProcessingStrategyChooseButton';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface NoLenderRequestIdProps {
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
}

export const NoLenderRequestId = ({ paymentMethodDetailEntity }: NoLenderRequestIdProps) => {
  const { customBorderRadius, palette } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();

  const { refreshCheckoutSessionAndPaymentMethodEntities } = useCheckoutSession();

  const lenderName = useMemo(() => paymentMethodDetailEntity.loan?.loanApplicationSelected?.lenderName, []);
  const loanApplicationSelected = useMemo(() => paymentMethodDetailEntity.loan?.loanApplicationSelected, []);
  const isBorrowerSelection = useMemo(() => paymentMethodDetailEntity.isBorrowerSelection(), []);
  // const hasLenderRequestId = paymentMethodDetailEntity.lenderRequestId !== undefined;

  if (isBorrowerSelection && !loanApplicationSelected) {
    return (
      <Box width="100%">
        <Alert
          severity="info"
          style={{ borderRadius: customBorderRadius.paper, padding: 16 }}
          icon={
            <Box display="flex" alignItems="center" justifyContent="center" width={22} height={22} flexShrink={0}>
              <Icon size={18} style={{ color: palette.info.main }}>
                ads_click
              </Icon>
            </Box>
          }
        >
          <Box display="flex" flexDirection={'column'} gridGap={16}>
            <Box>
              <Font variant="body2" display="block">
                <Text strong tId={'SELECT_A_LOAN_APPLICATION'} />
              </Font>
              <Font variant="body2" display="block">
                <Text tId={'SELECT_A_LOAN_APPLICATION_TEXT'} />
              </Font>
            </Box>
            <Box flexShrink={0}>
              <ProcessingStrategyChooseButton paymentMethodDetailEntity={paymentMethodDetailEntity} />
            </Box>
          </Box>
        </Alert>
      </Box>
    );
  }

  if (!lenderName || !loanApplicationSelected) {
    return (
      <Box width="100%">
        <Alert
          severity="info"
          style={{ borderRadius: customBorderRadius.paper, padding: 16 }}
          icon={
            <Box display="flex" alignItems="center" justifyContent="center" width={22} height={22} flexShrink={0}>
              <CircularProgress size={18} style={{ color: palette.info.main }} />
            </Box>
          }
        >
          <Box display="flex" flexDirection={'column'} gridGap={16}>
            <Box>
              <Font variant="body2" display="block">
                <Text strong tId={'PROCESSING_WITH_DOTS'} />
              </Font>
              <Font variant="body2" display="block">
                <Text tId={'LENDER_IS_PROCESSING_THE_APPLICATION_TRY_AGAIN_LATER'} />
              </Font>
            </Box>
            <Box flexShrink={0}>
              <RefreshIconButton
                variant="outlined"
                color="secondary"
                buttonType="button"
                onClick={refreshCheckoutSessionAndPaymentMethodEntities}
              />
            </Box>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box width={'100%'}>
      <Alert severity="warning" style={{ borderRadius: customBorderRadius.paper }}>
        <Box>
          <Font variant="body2" display="block">
            <Text strong tId={'NO_LENDER_REQUEST_ID_TITLE'} dynamicDataTexts={[getTextIdTranslation(lenderName)]} />
          </Font>
          <Font variant="body2" display="block">
            <Text tId={'NO_LENDER_REQUEST_ID_TITLE_TEXT'} dynamicDataTexts={[getTextIdTranslation(lenderName)]} />
          </Font>
          <Font variant="caption" display="block" style={{ marginTop: 4 }}>
            {formatDateTimeReadable(loanApplicationSelected.updatedAt)}
          </Font>
        </Box>
      </Alert>
    </Box>
  );
};
